import { ArrowLeft, Info, Loader2, PenTool, CheckCircle2 } from 'lucide-react';
import type { GeoJSONPolygon } from '@signalvote/shared';

import { Button } from '@/components/ui/button';
import {
  calculatePolygonArea,
  formatArea,
} from '@/services/turfCuttingService';
import { MAP_COLORS } from './mapColors';

interface TurfCutPanelProps {
  polygon: GeoJSONPolygon | null;
  previewStats: { houseCount: number; voterCount: number; overlapWarnings?: Array<{ walkbookName: string; overlappingHouseCount: number }> };
  isLoadingPreview: boolean;
  onClear: () => void;
  onSave: () => void;
  onCancel: () => void;
  editingTurfName?: string;
}

export function TurfCutPanel({
  polygon,
  previewStats,
  isLoadingPreview,
  onClear,
  onSave,
  onCancel,
  editingTurfName,
}: TurfCutPanelProps) {
  const isEditing = !!editingTurfName;
  const hasPolygon = polygon !== null;

  const houseCount = previewStats.houseCount;
  const voterCount = previewStats.voterCount;
  const overlapWarnings = previewStats.overlapWarnings ?? [];
  const area = hasPolygon ? calculatePolygonArea(polygon) : 0;

  return (
    <div className="absolute top-4 left-4 z-20 flex w-80 max-h-[calc(100vh-8rem)] flex-col rounded-2xl bg-white/95 shadow-[0_4px_24px_rgba(0,0,0,0.12)] backdrop-blur-sm">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        <button
          onClick={onCancel}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 transition-colors hover:bg-gray-200"
        >
          <ArrowLeft className="h-4 w-4 text-gray-700" />
        </button>
        <div className="min-w-0">
          <h2 className="text-base font-bold text-gray-900">
            {isEditing ? 'Edit Polygon' : 'Cut New Turf'}
          </h2>
          {isEditing && (
            <p className="truncate text-xs text-gray-500">{editingTurfName}</p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 space-y-3 overflow-y-auto px-4 pb-1">
        {/* Instructions */}
        <div className="flex items-start gap-2.5 rounded-xl bg-blue-50 p-3">
          <div className="mt-0.5 shrink-0">
            {hasPolygon ? (
              <CheckCircle2 className="h-4 w-4 text-blue-500" />
            ) : (
              <PenTool className="h-4 w-4 text-blue-500" />
            )}
          </div>
          <p className="text-sm leading-snug text-blue-700">
            {isEditing
              ? 'Drag vertices to reshape the polygon, or delete and redraw it.'
              : !hasPolygon
                ? 'Click on the map to draw a polygon around the houses you want to include in this turf.'
                : 'Polygon drawn. Review the preview below.'}
          </p>
        </div>

        {/* Preview Stats — horizontal pills */}
        {hasPolygon && (
          <div>
            {isLoadingPreview ? (
              <div className="flex items-center gap-2 py-2">
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                <span className="text-sm text-gray-500">Calculating...</span>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700">
                  {houseCount} houses
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700">
                  {voterCount} voters
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700">
                  {formatArea(area)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Overlap Warnings */}
        {overlapWarnings.length > 0 && (
          <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/80 p-3">
            <h4 className="text-xs font-semibold text-amber-800">Overlap Warnings</h4>
            {overlapWarnings.map((warning, i) => (
              <p key={i} className="text-xs leading-relaxed text-amber-700">
                {warning.overlappingHouseCount} houses overlap with &ldquo;{warning.walkbookName}&rdquo;
              </p>
            ))}
          </div>
        )}

        {/* Legend */}
        <div className="space-y-1.5">
          <h4 className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
            Legend
          </h4>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: MAP_COLORS.defaultBlue }} />
            Voter (not contacted)
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: MAP_COLORS.contactedOrange }} />
            Voter (contacted)
          </div>
        </div>
      </div>

      {/* Hint when a polygon is drawn but lands on no voter data — matches the Save button's
          disabled state so the manager knows WHY they can't save. */}
      {hasPolygon && houseCount === 0 && !isLoadingPreview && (
        <div className="flex items-start gap-1.5 px-4 pb-1 text-xs text-amber-600">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>Must be placed over voter data</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 px-4 pt-3 pb-4">
        <Button
          variant="outline"
          className="flex-1 rounded-xl"
          disabled={!hasPolygon}
          onClick={onClear}
        >
          Clear
        </Button>
        <Button
          className="flex-1 rounded-xl"
          disabled={!hasPolygon || (houseCount === 0 && !isLoadingPreview)}
          onClick={onSave}
        >
          {isEditing ? 'Update Polygon' : 'Save Turf'}
        </Button>
      </div>
    </div>
  );
}
